/**
 * Service layer — BIM Coordination domain (Supabase).
 *
 * MIGRATED off Airtable (database/111_bim.sql + scripts/import_bim.js): systems,
 * elements with per-phase criteria checklists, and the template library all live in
 * Supabase behind api/bim.js. This file is the ONLY place the rest of the app touches
 * BIM data — callers were unaffected by the swap.
 *
 * NO ASSIGNMENT SURFACE. The system-RASCI accessors and the element/criterion
 * delegation setters were removed when RASCI was retired 2026-09-02
 * (docs/FLOW_RECONCILIATION_PLAN.md decisions 5 + 6), and nothing replaces them:
 * crews are the only assignment mechanism and they are set on the Flight Board only.
 *
 * Compat note: getProjectBimSystems returns Supabase rows AUGMENTED with a `fields`
 * object ({ Name, Status, 'Slack Thread Timestamp' }) so pre-migration consumers keep
 * rendering; new code reads the top-level columns + `stats` instead.
 */

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = '/api/bim' + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'BIM request failed: ' + res.status)
  }
  return res.json()
}

/** Coordination phases, in process order. CD exists in the schema but currently has
 *  no criteria anywhere (CD is drawing-production, not coordination) — the UI shows
 *  only phases that actually carry criteria. */
export const BIM_PHASES = ['sd', 'dd', 'cd', 'ia']
export const BIM_PHASE_LABELS = { sd: 'SD', dd: 'DD', cd: 'CD', ia: 'IA' }

/** Derived system status label — replaces the old Airtable Status formula. */
export function deriveSystemStatus(stats) {
  if (!stats || !stats.elements) return 'No Elements'
  const eligible = BIM_PHASES.filter(p => stats.phase[p] && stats.phase[p].eligible > 0)
  if (eligible.length && eligible.every(p => stats.phase[p].complete >= stats.phase[p].eligible)) return 'Complete'
  if (stats.started > 0) return 'In Progress'
  return 'Not Started'
}

/**
 * All BIM Systems for a project, sorted by template order. Each row carries `stats`
 * (derived per-phase eligible/complete/criteria counts + percent) and the legacy
 * `fields` compat object.
 */
export async function getProjectBimSystems(projectId) {
  if (!projectId) return []
  const d = await call('GET', null, 'projectId=' + encodeURIComponent(projectId))
  return (d.systems || []).map(s => ({
    ...s,
    fields: {
      'Name': s.name,
      'Status': deriveSystemStatus(s.stats),
      'Slack Thread Timestamp': s.slack_thread_ts || '',
    },
  }))
}

/**
 * One system's elements with merged per-phase criteria:
 * { system, elements: [{ ...row, status, criteria: { sd: [{id,text,kind,done,by_name,at}], … } }] }
 */
/**
 * "Set Up BIM Model" — instantiate one or more systems (+ their ready-for-use
 * elements) onto a project. items: [{ systemTemplateId, name? }]. Returns the
 * created system rows (each with an `elementCount`).
 */
export function addBimSystems(projectId, items) {
  return call('POST', { action: 'addSystems', projectId, items }).then(d => d.systems || [])
}

/**
 * Add more elements to a system that's already been set up (e.g. the template gained new
 * elements since). Returns the created element rows (empty criteria — a normal new element).
 */
export function addBimElements(systemId, elementTemplateIds) {
  return call('POST', { action: 'addElements', systemId, elementTemplateIds }).then(d => d.elements || [])
}

export function getBimSystemElements(systemId) {
  return call('GET', null, 'systemId=' + encodeURIComponent(systemId))
}

/** One element (merged criteria) + its parent system — deep-linked element zoom. */
export function getBimElement(elementId) {
  return call('GET', null, 'elementId=' + encodeURIComponent(elementId))
}

/** Check/uncheck one criterion on an element. Returns the updated element row. */
export function toggleBimCriterion(elementId, criterionId, done, actorMemberId, actorName) {
  return call('POST', { action: 'toggleCriterion', elementId, criterionId, done, actorMemberId, actorName }).then(d => d.element)
}

/** Mark an element Started (records actor + timestamp). */
export function markBimElementStarted(elementId, actorMemberId, actorName) {
  return call('POST', { action: 'markStarted', elementId, actorMemberId, actorName }).then(d => d.element)
}

/** Complete (or un-complete) one phase on an element — independent of criteria state. */
export function markBimPhaseComplete(elementId, phase, complete, actorName) {
  return call('POST', { action: 'markPhaseComplete', elementId, phase, complete, actorName }).then(d => d.element)
}

/** Set/clear an element's memo. */
export function setBimElementMemo(elementId, memo) {
  return call('POST', { action: 'setMemo', elementId, memo }).then(d => d.element)
}

/* ── Template library (BIM tab of TemplatesWorkspace) ────────────────────────── */

/** Full template tree: [{ ...systemTmpl, elements: [{ ...elementTmpl, criteria: [...] }] }] */
export function getBimTemplates() {
  return call('GET', null, 'templates=1').then(d => d.systems || [])
}

export function saveBimSystemTemplate(id, fields) {
  return call('POST', { action: 'saveSystemTemplate', id: id || undefined, fields }).then(d => d.template)
}

export function saveBimElementTemplate(id, fields) {
  return call('POST', { action: 'saveElementTemplate', id: id || undefined, fields }).then(d => d.template)
}

export function deleteBimElementTemplate(id) {
  return call('POST', { action: 'deleteElementTemplate', id }).then(() => true)
}

/** Create (no id) or update (with id) a criterion. */
export function saveBimCriterion({ id, elementTemplateId, phase, text, kind, sortOrder } = {}) {
  return call('POST', { action: 'saveCriterion', id, elementTemplateId, phase, text, kind, sortOrder }).then(d => d.criterion)
}

export function deleteBimCriterion(id) {
  return call('POST', { action: 'deleteCriterion', id }).then(() => true)
}

/** Persist a drag-reorder — ids in display order (one template+phase at a time). */
export function reorderBimCriteria(ids) {
  return call('POST', { action: 'reorderCriteria', ids }).then(() => true)
}

/* ── Visual Aids / Video Tutorials (per element template, per phase) ─────────── */

/**
 * Upload a Visual Aid image for one phase of an element template, then record it on the
 * template. Returns the updated template row ({ ...fields, visual_aids }).
 */
export async function uploadBimVisualAid(elementTemplateId, phase, file) {
  const token = await window.__clerkGetToken()
  const credRes = await fetch('/api/upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify({ action: 'bimVisualAidUploadUrl', elementTemplateId, phase, filename: file.name }),
  })
  if (!credRes.ok) {
    const b = await credRes.json().catch(() => ({}))
    throw new Error(b.error || 'Could not get upload URL (' + credRes.status + ')')
  }
  const { signedUrl, publicUrl } = await credRes.json()
  const uploadRes = await fetch(signedUrl, {
    method: 'PUT', headers: { 'Content-Type': file.type || 'image/png', 'x-upsert': 'true' }, body: file,
  })
  if (!uploadRes.ok) throw new Error('Storage upload failed (' + uploadRes.status + ')')
  return call('POST', { action: 'addVisualAid', elementTemplateId, phase, url: publicUrl }).then(d => d.template)
}

export function removeBimVisualAid(elementTemplateId, phase, url) {
  return call('POST', { action: 'removeVisualAid', elementTemplateId, phase, url }).then(d => d.template)
}

/** Admin: one-time carry-over of the historical Visual Aid images + Video Tutorial URLs. */
export function backfillBimVisualAids() {
  return call('POST', { action: 'backfillVisualAids' })
}

/** Admin: copy template Sample Images from Airtable into the bim-templates bucket. */
export function backfillBimTemplateImages() {
  return call('POST', { action: 'backfillImages' })
}

/** @deprecated pre-migration alias — BIM systems no longer live in Airtable. */
export { getProjectBimSystems as getProjectBimSystemsLegacy }
